import { useState } from "react";
import { Link2, Unlink, Check, X } from "lucide-react";
import { trpc } from "../../hooks/trpc";

// =====================================================================
// Spalte „Person“ der Benutzertabelle (Migration 0049, Claim personUuid).
//
// Die feste Zuordnung Konto zu Person ist ein Attribut des Benutzerkontos
// und wird deshalb direkt in der Benutzerverwaltung gepflegt (frueher eigene
// Ansicht „Nutzer und Personen“, Brief 2026-11-04). Der Server erzwingt GF
// (personLink-Router, gfProcedure, sonst 403); ohne `canLink` ist die Zelle
// nur lesend. Jede Aenderung auditiert der Server (PERSON_LINKED/UNLINKED).
// =====================================================================

const STROKE = 1.6;

type PersonTyp = "intern" | "extern";

export interface PersonLinkRow {
  userId: number;
  username: string;
  person: {
    personTyp: PersonTyp;
    personId: string;
    vorname: string | null;
    nachname: string | null;
    mandantId: string | null;
    status: string | null;
  } | null;
  emailTreffer: number;
  emailKandidat?: {
    personTyp: PersonTyp;
    personId: string;
    vorname: string | null;
    nachname: string | null;
    mandantId: string | null;
  } | null;
}

export function personLabel(p: { vorname: string | null; nachname: string | null }): string {
  return [p.nachname, p.vorname].filter(Boolean).join(", ") || "Ohne Namen";
}

const muted = { color: "var(--color-muted)", fontFamily: "var(--font-mono)" } as const;
// `.btn` setzt Hoehe und Polster fest; in der Tabellenzelle bewusst kleiner.
const SMALL_BTN = { padding: "2px 8px", fontSize: 11, minHeight: 0, height: "auto", lineHeight: 1.4, gap: 4 } as const;

/** Lesende und bedienende Darstellung der Verknuepfung in der Tabellenzelle. */
export function UserPersonCell({
  row,
  canLink,
  busy,
  searching,
  onSearch,
  onLink,
  onUnlink,
}: {
  row: PersonLinkRow | undefined;
  canLink: boolean;
  busy: boolean;
  searching: boolean;
  onSearch: () => void;
  onLink: (ref: { personTyp: PersonTyp; personId: string }) => void;
  onUnlink: () => void;
}) {
  if (!row) return <span className="text-xs" style={muted}>—</span>;
  const id = row.userId;

  if (row.person) {
    const p = row.person;
    return (
      <div data-testid={`admin-user-person-${id}`}>
        <div className="text-xs">{personLabel(p)}</div>
        <div className="text-[11px]" style={muted}>
          {p.personTyp} · {p.mandantId ?? "ohne Mandant"}
          {p.status && p.status !== "aktiv" ? ` · ${p.status}` : ""}
        </div>
        {canLink && (
          <div className="flex gap-1 mt-1">
            <button
              type="button"
              className="btn btn-secondary"
              style={SMALL_BTN}
              onClick={onSearch}
              aria-pressed={searching}
              data-testid={`admin-person-link-select-${id}`}
            >
              <Link2 size={11} strokeWidth={STROKE} /> Ändern
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={SMALL_BTN}
              disabled={busy}
              onClick={() => {
                if (confirm(`Verknüpfung von "${row.username}" lösen? Neue App-Starts tragen dann keinen oder nur den E-Mail-Claim.`)) onUnlink();
              }}
              data-testid={`admin-person-unlink-${id}`}
            >
              <Unlink size={11} strokeWidth={STROKE} /> Lösen
            </button>
          </div>
        )}
      </div>
    );
  }

  const kandidat = row.emailKandidat ?? null;
  return (
    <div data-testid={`admin-user-person-${id}`}>
      <div className="text-[11px]" style={muted}>nicht verknüpft</div>
      {kandidat ? (
        <div className="text-[11px] mt-1" data-testid={`admin-person-hint-${id}`} style={{ color: "var(--color-warning, var(--color-primary))" }}>
          Gleiche E-Mail: {personLabel(kandidat)} ({kandidat.personTyp} · {kandidat.mandantId ?? "ohne Mandant"})
          {canLink && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ ...SMALL_BTN, marginLeft: 6 }}
              disabled={busy}
              onClick={() => onLink({ personTyp: kandidat.personTyp, personId: kandidat.personId })}
              data-testid={`admin-person-adopt-${id}`}
            >
              <Check size={11} strokeWidth={STROKE} /> Übernehmen
            </button>
          )}
        </div>
      ) : row.emailTreffer > 1 ? (
        <div className="text-[11px] mt-1" style={muted}>{row.emailTreffer} Personen mit gleicher E-Mail (mehrdeutig)</div>
      ) : null}
      {canLink && (
        <button
          type="button"
          className="btn btn-secondary"
          style={{ ...SMALL_BTN, marginTop: 4 }}
          onClick={onSearch}
          aria-pressed={searching}
          data-testid={`admin-person-link-select-${id}`}
        >
          <Link2 size={11} strokeWidth={STROKE} /> Verknüpfen
        </button>
      )}
    </div>
  );
}

/** Inline-Suche unter der Tabellenzeile (Stammdatenpersonen intern und extern). */
export function PersonSearchRow({
  userId,
  label,
  busy,
  onLink,
  onClose,
}: {
  userId: number;
  label: string;
  busy: boolean;
  onLink: (ref: { personTyp: PersonTyp; personId: string }) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const searchQuery = trpc.personLink.search.useQuery(
    { search },
    { enabled: search.trim().length >= 2 },
  );
  const results = searchQuery.data ?? [];
  return (
    <div data-testid="admin-person-link-search" className="p-3" style={{ background: "var(--color-surface)" }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold">Person für {label} wählen</span>
        <button type="button" className="btn btn-secondary"
          style={{ ...SMALL_BTN, marginLeft: "auto" }} onClick={onClose} aria-label="Suche schließen">
          <X size={11} strokeWidth={STROKE} /> Abbrechen
        </button>
      </div>
      <input
        type="search"
        className="input"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Name oder E-Mail (mind. 2 Zeichen)"
        aria-label="Person suchen"
        autoFocus
        data-testid="admin-person-link-input"
      />
      <div className="mt-2">
        {results.map((p) => {
          const vergeben = p.linkedUserId !== null && p.linkedUserId !== userId;
          return (
            <div key={`${p.personTyp}-${p.personId}`} className="flex items-center gap-3 py-1 text-xs" style={{ borderBottom: "1px solid var(--color-border)" }}>
              <span style={{ flex: 1.4 }}>
                {personLabel(p)} <span className="text-[11px]" style={muted}>{p.personTyp} · {p.mandantId ?? "ohne Mandant"} · {p.status ?? "—"}</span>
              </span>
              <span style={{ flex: 1.4, ...muted }} className="text-[11px]">{p.email ?? "ohne E-Mail"}</span>
              <span style={{ flex: 1, ...muted }} className="text-[11px]">{p.linkedUsername ? `zugeordnet: @${p.linkedUsername}` : "frei"}</span>
              <button
                type="button"
                className="btn btn-secondary"
                style={SMALL_BTN}
                disabled={vergeben || busy}
                title={vergeben ? "Bereits einem anderen Nutzer zugeordnet" : undefined}
                onClick={() => onLink({ personTyp: p.personTyp, personId: p.personId })}
              >
                Zuordnen
              </button>
            </div>
          );
        })}
        {searchQuery.data && searchQuery.data.length === 0 ? (
          <div className="text-xs py-1" style={muted}>Keine Person gefunden.</div>
        ) : null}
      </div>
    </div>
  );
}
